// frontend/src/components/FinanceReport/Accounting/components/accounts/company-accounts/CompanyAccountsTab.jsx

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAllProfitAllocations,
  getAllCurrencies,
  getAllAccounts,
  deleteAccount,
} from "./requests";
import AccountFormModal from "./AccountFormModal";

const RATES_URL = "https://open.er-api.com/v6/latest/USD";
const TRON_URL = "https://api.binance.com/api/v3/ticker/price?symbol=TRXUSDT";

const currencySymbols = {
  USD: "$",
  RUB: "₽",
  CNY: "¥",
  USDT: "₮",
  TRX: "TRX",
};

const tickerStyles = `
.ticker-wrapper { width: 100%; overflow: hidden; background: #0b0f19; border-radius: 12px; padding: 10px 0; margin-bottom: 20px; }
.ticker-track { display: flex; width: max-content; animation: scroll 20s linear infinite; gap: 40px; white-space: nowrap; }
.ticker-item { color: #00ff88; font-family: monospace; font-weight: 600; font-size: 14px; padding: 4px 10px; border-radius: 6px; transition: all 0.2s; }
.ticker-item:hover { background: rgba(0,255,136,0.1); transform: scale(1.05); }
@keyframes scroll { 0% { transform: translateX(0%); } 100% { transform: translateX(-50%); } }
`;

export default function CompanyAccountsTab() {
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState([]);
  const [items, setItems] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [rates, setRates] = useState(null);
  const [tronPrice, setTronPrice] = useState(null);
  const [displayCurrency, setDisplayCurrency] = useState("CNY");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [accountModal, setAccountModal] = useState({
    open: false,
    mode: "create",
    item: null,
  });
  const [accountToDelete, setAccountToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [accList, list, curList] = await Promise.all([
        getAllAccounts(),
        getAllProfitAllocations(),
        getAllCurrencies().catch(() => []),
      ]);
      setAccounts(accList);
      setItems(list);
      setCurrencies(curList);
    } catch (err) {
      setError(err.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    fetch(RATES_URL)
      .then((r) => r.json())
      .then((d) => setRates(d.rates))
      .catch(console.error);
    fetch(TRON_URL)
      .then((r) => r.json())
      .then((d) => setTronPrice(Number(d.price)))
      .catch(console.error);
  }, []);

  const trxPerUsd = tronPrice ? 1 / Number(tronPrice) : null;

  const currenciesById = useMemo(() => {
    const map = {};
    currencies.forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [currencies]);

  const toUSD = (amount, from) => {
    if (!rates || !trxPerUsd) return 0;
    if (from === "TRX") return amount / Number(trxPerUsd);
    if (from === "USD") return amount;
    if (from === "USDT") return amount;
    return amount / (rates[from] || 1);
  };

  const fromUSD = (amountUSD, to) => {
    if (!rates || !trxPerUsd) return 0;
    if (to === "USDT") return amountUSD;
    if (to === "USD") return amountUSD;
    if (to === "TRX") return amountUSD * trxPerUsd;
    return amountUSD * (rates[to] || 1);
  };

  const convert = (amount, from, to) => fromUSD(toUSD(amount, from), to);

  // Баланс счёта: только подтверждённые (PAID) движения.
  // income → +amount, expense/dividends → −amount
  const balanceOf = (accId, accCurrency) => {
    let bal = 0;
    items.forEach((p) => {
      if (String(p.account) !== String(accId)) return;
      if (p.payment_status !== "PAID") return; // ← pending не влияет на баланс
      const amt = Number(p.amount || 0);
      if (p.type === "income") bal += amt;
      else if (p.type === "expense" || p.type === "dividends") bal -= amt;
    });
    return bal;
  };

  const totalInSelectedCurrency = useMemo(() => {
    const totalUSD = accounts.reduce((sum, acc) => {
      const bal = balanceOf(acc.id, acc.currency);
      return sum + toUSD(bal, acc.currency);
    }, 0);
    return fromUSD(totalUSD, displayCurrency);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, items, currenciesById, rates, tronPrice, displayCurrency]);

  const displayCurrencyOptions = useMemo(() => {
    const codes = new Set(["USD", "RUB", "CNY", "USDT", "TRX"]);
    currencies.forEach((c) => {
      if (c.code) codes.add(c.code);
    });
    return Array.from(codes);
  }, [currencies]);

  const handleDelete = async () => {
    if (!accountToDelete) return;
    try {
      await deleteAccount(accountToDelete.id);
      setAccountToDelete(null);
      await load();
    } catch (err) {
      setError(err.message || "Не удалось удалить счёт");
    }
  };

  const handleSaved = async () => {
    setAccountModal({ open: false, mode: "create", item: null });
    await load();
  };

  if (loading) return <div className="p-4 text-slate-500">Загрузка…</div>;

  return (
    <>
      <style>{tickerStyles}</style>

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Счета компании</h2>
        <button
          type="button"
          onClick={() =>
            setAccountModal({ open: true, mode: "create", item: null })
          }
          className="rounded-lg bg-black text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800"
        >
          + Создать
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="p-4 border rounded-xl mb-6 bg-gray-50">
        <p className="text-sm text-gray-500">Общий баланс</p>
        <p className="text-2xl font-bold text-black">
          {currencySymbols[displayCurrency] || displayCurrency}{" "}
          {totalInSelectedCurrency.toFixed(2)}
        </p>
        <select
          value={displayCurrency}
          onChange={(e) => setDisplayCurrency(e.target.value)}
          className="border p-2 rounded mt-2"
        >
          {displayCurrencyOptions.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
      </div>

      <div className="ticker-wrapper">
        <div className="ticker-track">
          <div className="ticker-item">USD {rates?.USD?.toFixed(2) || "—"}</div>
          <div className="ticker-item">USD/CNY {rates?.CNY?.toFixed(2) || "—"}</div>
          <div className="ticker-item">USD/RUB {rates?.RUB?.toFixed(2) || "—"}</div>
          <div className="ticker-item">TRX/USDT {tronPrice?.toFixed(3) || "—"}</div>
          <div className="ticker-item">USDT/TRX {trxPerUsd?.toFixed(3) || "—"}</div>
          <div className="ticker-item">USD {rates?.USD?.toFixed(2) || "—"}</div>
          <div className="ticker-item">USD/CNY {rates?.CNY?.toFixed(2) || "—"}</div>
          <div className="ticker-item">USD/RUB {rates?.RUB?.toFixed(2) || "—"}</div>
          <div className="ticker-item">TRX/USDT {tronPrice?.toFixed(3) || "—"}</div>
          <div className="ticker-item">USDT/TRX {trxPerUsd?.toFixed(3) || "—"}</div>
        </div>
      </div>

      <div className="space-y-4">
        {accounts.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
            <div className="text-3xl">🏦</div>
            <p className="mt-2 font-semibold text-slate-800">Счетов пока нет</p>
            <p className="mt-1 text-sm text-slate-500">
              Нажмите «+ Создать», чтобы создать первый.
            </p>
          </div>
        ) : (
          accounts.map((acc) => {
            const bal = balanceOf(acc.id, acc.currency);
            return (
              <div
                key={acc.id}
                onClick={() =>
                  navigate(`/accounting/company-accounts/${acc.id}`)
                }
                className="flex justify-between items-start p-4 rounded-xl border bg-white cursor-pointer transition hover:shadow-sm"
              >
                <div>
                  <p className="font-semibold text-lg">{acc.name}</p>
                  <p className="text-lg font-medium">
                    {currencySymbols[acc.currency] || acc.currency}{" "}
                    {bal.toFixed(2)}
                  </p>
                  <p className="text-gray-500">{acc.comment || "-"}</p>
                  <p className="text-sm text-gray-400">
                    ≈ {convert(bal, acc.currency, displayCurrency).toFixed(2)}{" "}
                    {displayCurrency}
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAccountModal({
                        open: true,
                        mode: "edit",
                        item: acc,
                      });
                    }}
                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Редактировать
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAccountToDelete(acc);
                    }}
                    className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                  >
                    Удалить
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <AccountFormModal
        open={accountModal.open}
        mode={accountModal.mode}
        initial={accountModal.item}
        onClose={() =>
          setAccountModal({ open: false, mode: "create", item: null })
        }
        onSaved={handleSaved}
      />

      {accountToDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-96">
            <h3 className="text-lg font-bold mb-4">Удалить счёт?</h3>
            <p className="mb-6 text-gray-600">{accountToDelete.name}</p>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 border rounded"
                onClick={() => setAccountToDelete(null)}
              >
                Отмена
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded"
                onClick={handleDelete}
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}